import { useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Activity } from "lucide-react";

/**
 * BingoCheckOverlay — the Bingo Check · Heartbeat Verification screen.
 *
 * Signature moment of the IC FunFlow Bingo Show. When a guest claims
 * BINGO, this overlay slides in over the whole projector and puts the
 * room into a calm, cinematic verification mode:
 *
 *   • The rest of the show visually recedes (deep dim + soft blur).
 *   • A steady heartbeat glow pulses at ~60 bpm around the frame.
 *   • The claimed line of numbers appears in a horizontal row.
 *   • The host verifies one number at a time — each correct number
 *     gets a gentle green tick with a soft confirmation glow.
 *   • The heartbeat continues throughout the verification.
 *   • The moment the LAST number is confirmed, the heartbeat stops
 *     instantly. That hard silence is the cue for the Confirmed Bingo
 *     Celebration that follows (built in the next module).
 *
 * This component is a pure PRESENTATIONAL view — parents drive it
 * with `verifiedCount`. It never plays audio itself (the parent owns
 * the heartbeat lifecycle so it can be reused across preview + live).
 */

const DEFAULT_HEARTBEAT_BPM = 60;
export const HEARTBEAT_PERIOD_MS = (60 * 1000) / DEFAULT_HEARTBEAT_BPM; // 1000ms — legacy constant kept for external callers

/**
 * @param {Object} props
 * @param {boolean} props.active
 * @param {number[]} props.numbers                 Claimed line (1..90)
 * @param {number} props.verifiedCount             How many have been ticked so far
 * @param {number} [props.heartbeatBpm=60]         Current visual heartbeat rate (drives pulse cadence)
 * @param {string} [props.playerLabel]             Optional label shown above the row
 * @param {string} [props.testidPrefix="bingo-check"]
 */
export default function BingoCheckOverlay({
  active,
  numbers = [],
  verifiedCount = 0,
  heartbeatBpm = DEFAULT_HEARTBEAT_BPM,
  playerLabel = "Claimed Line",
  testidPrefix = "bingo-check",
}) {
  const total = numbers.length;
  const done = Math.max(0, Math.min(verifiedCount, total));
  const isComplete = done === total && total > 0;
  const beatPeriodSec = 60 / Math.max(30, heartbeatBpm);

  // Once verification is complete, freeze the heartbeat visual so the
  // silence is felt (the audio side does the same via stopHeartbeat).
  const pulseAnimate = isComplete
    ? { opacity: 0 }
    : {
        opacity: [0.55, 0.15, 0.55, 0.15],
      };

  return (
    <AnimatePresence>
      {active && (
        <motion.div
          data-testid={`${testidPrefix}-overlay`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          className="fixed inset-0 z-[70] flex flex-col items-center justify-center px-8"
          style={{
            /* Automatic room blackout — dim the projector background to
               ~25% brightness. The verification card sits above this so
               it remains crisp while the room visually recedes. */
            background: "rgba(0, 0, 0, 0.75)",
            backdropFilter: "blur(14px) brightness(0.35)",
            WebkitBackdropFilter: "blur(14px) brightness(0.35)",
          }}
        >
          {/* Ambient heartbeat glow — a soft crimson pulse around the
              screen edges. Steady, calm, never aggressive. */}
          <motion.div
            aria-hidden
            className="absolute inset-0 pointer-events-none"
            animate={pulseAnimate}
            transition={{
              duration: isComplete ? 0.2 : beatPeriodSec * 2,
              times: isComplete ? undefined : [0, 0.12, 0.5, 1],
              repeat: isComplete ? 0 : Infinity,
              ease: "easeInOut",
            }}
            style={{
              boxShadow:
                "inset 0 0 220px 40px rgba(220, 38, 38, 0.35), inset 0 0 90px 10px rgba(220, 38, 38, 0.18)",
            }}
          />

          {/* Centered verification card */}
          <div className="relative w-full max-w-6xl flex flex-col items-center">
            <Header
              total={total}
              done={done}
              isComplete={isComplete}
              beatPeriodSec={beatPeriodSec}
            />

            {/* Numbers row — claimed line to verify */}
            <div className="mt-10 flex items-center gap-6 sm:gap-8">
              {numbers.map((n, idx) => (
                <VerifiedNumberTile
                  key={`${n}-${idx}`}
                  value={n}
                  verified={idx < done}
                  isActive={idx === done && !isComplete}
                  testid={`${testidPrefix}-tile-${idx}`}
                />
              ))}
            </div>

            {/* Player label */}
            <div
              className="mt-8 text-xs tracking-[0.4em] uppercase select-none"
              style={{ color: "rgba(255,255,255,0.55)" }}
              data-testid={`${testidPrefix}-player-label`}
            >
              {playerLabel}
            </div>
          </div>

          {/* Bottom-center progress */}
          <ProgressStrip done={done} total={total} isComplete={isComplete} />
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/* -------------------- Internals -------------------- */

function Header({ total, done, isComplete, beatPeriodSec }) {
  return (
    <div className="flex flex-col items-center gap-3">
      <div className="flex items-center gap-3">
        <motion.span
          animate={
            isComplete
              ? { scale: 1 }
              : { scale: [1, 1.15, 1, 1.15, 1] }
          }
          transition={{
            duration: beatPeriodSec * 2,
            times: [0, 0.12, 0.5, 0.62, 1],
            repeat: isComplete ? 0 : Infinity,
            ease: "easeInOut",
          }}
          className="inline-flex items-center justify-center rounded-full"
          style={{
            width: 44,
            height: 44,
            background:
              "radial-gradient(circle at 50% 45%, rgba(255, 80, 80, 0.9) 0%, rgba(220, 38, 38, 0.7) 60%, rgba(0,0,0,0) 100%)",
            boxShadow:
              "0 0 26px rgba(220, 38, 38, 0.55), 0 0 60px rgba(220, 38, 38, 0.25)",
          }}
          data-testid="bingo-check-heartbeat-dot"
        >
          <Activity className="w-5 h-5 text-white" strokeWidth={2.5} />
        </motion.span>
        <h1
          className="ic-font-display uppercase select-none"
          style={{
            fontSize: "clamp(1.6rem, 2.6vw, 2.6rem)",
            letterSpacing: "0.38em",
            fontWeight: 900,
            color: "rgba(255,255,255,0.94)",
            textShadow: "0 0 24px rgba(220,38,38,0.35)",
          }}
        >
          Bingo Check
        </h1>
      </div>
      <p
        className="text-xs sm:text-sm tracking-[0.32em] uppercase select-none"
        style={{ color: "rgba(255,255,255,0.55)" }}
      >
        Heartbeat Verification · {done}/{total}
      </p>
    </div>
  );
}

function VerifiedNumberTile({ value, verified, isActive, testid }) {
  // Palette
  const restingBg =
    "linear-gradient(180deg, rgba(28, 28, 34, 0.95) 0%, rgba(18, 18, 22, 0.95) 100%)";
  const verifiedBg =
    "radial-gradient(circle at 50% 40%, rgba(74, 222, 128, 0.28) 0%, rgba(16, 46, 27, 0.98) 70%)";
  const activeBg =
    "radial-gradient(circle at 50% 40%, rgba(220, 38, 38, 0.22) 0%, rgba(24, 8, 8, 0.98) 70%)";

  return (
    <motion.div
      data-testid={testid}
      layout
      initial={{ opacity: 0, y: 24, scale: 0.85 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ type: "spring", stiffness: 220, damping: 22 }}
      className="relative rounded-2xl flex items-center justify-center"
      style={{
        width: "clamp(4.5rem, 8vw, 8rem)",
        height: "clamp(6rem, 10vw, 10rem)",
        background: verified ? verifiedBg : isActive ? activeBg : restingBg,
        border: `2px solid ${
          verified
            ? "rgba(74, 222, 128, 0.85)"
            : isActive
              ? "rgba(220, 38, 38, 0.6)"
              : "rgba(255,255,255,0.12)"
        }`,
        boxShadow: verified
          ? "0 0 30px rgba(74, 222, 128, 0.45), inset 0 0 22px rgba(74, 222, 128, 0.18)"
          : isActive
            ? "0 0 26px rgba(220, 38, 38, 0.45)"
            : "inset 0 -4px 12px rgba(0,0,0,0.6)",
      }}
    >
      <span
        className="ic-font-display tabular-nums select-none"
        style={{
          fontSize: "clamp(2rem, 4vw, 3.6rem)",
          fontWeight: 900,
          letterSpacing: "-0.02em",
          color: verified
            ? "rgba(220, 252, 231, 0.98)"
            : isActive
              ? "rgba(255, 235, 235, 0.98)"
              : "rgba(255,255,255,0.9)",
          textShadow: verified
            ? "0 0 14px rgba(74, 222, 128, 0.55)"
            : "0 0 10px rgba(0,0,0,0.4)",
        }}
      >
        {value}
      </span>

      {/* Green tick badge — appears on verify */}
      <AnimatePresence>
        {verified && (
          <motion.div
            key="tick"
            initial={{ opacity: 0, scale: 0.4 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ type: "spring", stiffness: 380, damping: 22 }}
            className="absolute -top-3 -right-3 rounded-full inline-flex items-center justify-center"
            style={{
              width: 32,
              height: 32,
              background:
                "linear-gradient(160deg, #4ADE80 0%, #16A34A 100%)",
              boxShadow:
                "0 0 20px rgba(74, 222, 128, 0.65), 0 2px 6px rgba(0,0,0,0.35)",
              border: "2px solid rgba(255,255,255,0.35)",
            }}
            data-testid={`${testid}-tick`}
          >
            <Check className="w-4 h-4 text-white" strokeWidth={3.5} />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Soft red underline pulse for the active number */}
      {isActive && (
        <motion.div
          aria-hidden
          className="absolute bottom-2 left-1/2 h-[3px] rounded-full"
          style={{
            background:
              "linear-gradient(90deg, transparent 0%, rgba(220,38,38,0.9) 50%, transparent 100%)",
            width: "70%",
            transform: "translateX(-50%)",
          }}
          animate={{ opacity: [0.55, 1, 0.55] }}
          transition={{ duration: 1, repeat: Infinity, ease: "easeInOut" }}
        />
      )}
    </motion.div>
  );
}

function ProgressStrip({ done, total, isComplete }) {
  const dots = useMemo(
    () => Array.from({ length: total }, (_, i) => i),
    [total],
  );
  return (
    <div className="absolute bottom-12 left-1/2 -translate-x-1/2 flex items-center gap-2.5">
      {dots.map((i) => {
        const filled = i < done;
        return (
          <span
            key={i}
            data-testid={`bingo-check-progress-dot-${i}`}
            className="rounded-full transition-all"
            style={{
              width: filled ? 26 : 10,
              height: 10,
              background: filled
                ? "linear-gradient(90deg, #4ADE80 0%, #16A34A 100%)"
                : isComplete
                  ? "rgba(74,222,128,0.55)"
                  : "rgba(255,255,255,0.18)",
              boxShadow: filled
                ? "0 0 12px rgba(74,222,128,0.55)"
                : "none",
            }}
          />
        );
      })}
    </div>
  );
}
